import clsx from "clsx";
import { formatDuration, intervalToDuration } from "date-fns";
import { FC, ReactNode } from "react";
import {
  AssetLabel,
  assetLabel,
  Decimal,
  IconDenom,
  Icons,
  Progress,
  useTranslation,
} from "rujira.ui";
import { Side } from "../../types";
import { formatExecutionDuration } from "../../utils";
import { PairLink } from "../Orders/Common";
import { LabelStatus } from "../Orders/shared/labels";
import { RemovableColumn } from "../Orders/shared/recurring";
import {
  RecurringOrder,
  RecurringOrderConfig,
} from "../Orders/recurringOrderModel";
import { OrderRow, OrderRowDetail } from "./OrderRow";

type RowConfig = RecurringOrderConfig;

export const RecurringOrderRow: FC<{
  order: RecurringOrder;
  config: RowConfig;
  hide?: RemovableColumn[];
  tradeTo: string;
  swapProgress: number;
  remainingDuration?: number;
  nextExecution: string;
  secondsUntilNextExecution: number;
  priceThreshold: bigint;
  swapAdjustmentDescription: string | null;
  actions: ReactNode;
  claimButton?: ReactNode;
}> = ({
  order,
  config,
  hide,
  tradeTo,
  swapProgress,
  remainingDuration,
  nextExecution,
  secondsUntilNextExecution,
  priceThreshold,
  swapAdjustmentDescription,
  actions,
  claimButton,
}) => {
  const { t } = useTranslation();
  const {
    swapAmount,
    minimumReceiveAmount,
    swapAdjustment,
    routes,
    cadence,
    side,
    isSkipping,
    remainingSwapBalance,
    acquiredReceiveAmount,
    remainingSwaps,
    maximumSlippageBps,
  } = config;

  const stat = (v: ReactNode) => <span className="mono">{v}</span>;

  const label = (asset: typeof swapAmount.asset) => (
    <AssetLabel
      asset={asset}
      Container={({ children }) => (
        <small className="color-grey fs-11">{children}</small>
      )}
    />
  );

  const completed = remainingSwaps === 0;

  const progressAside = (
    <span className="order-row__progress">
      <Progress height={5} percentage={swapProgress} />
      <span className="mono fs-12 color-grey">{swapProgress}%</span>
    </span>
  );

  const swapAsset = (v: bigint) => (
    <Decimal
      subscript
      amount={v}
      decimals={swapAmount.asset.metadata.decimals}
      symbol={swapAmount.asset.metadata.symbol}
      symbolClassName="decimal__symbol--small color-grey condensed fw-500"
      clip
    />
  );

  const details: OrderRowDetail[] = [
    ...(hide?.includes("Time Remaining")
      ? []
      : [
          {
            label: t("timeRemaining"),
            value: !remainingDuration ? (
              ""
            ) : isSkipping ? (
              <span className="mono color-darkOrange">{t("halted")}</span>
            ) : (
              stat(
                formatExecutionDuration(
                  intervalToDuration({
                    start: 0,
                    end: Number(remainingDuration) * 1000,
                  })
                )
              )
            ),
          },
        ]),
    {
      label: t("every"),
      value: stat(
        "interval" in cadence
          ? cadence.interval > 1
            ? `${cadence.interval.toLocaleString()} blocks`
            : "1 block"
          : cadence.duration
            ? formatDuration(
                intervalToDuration({
                  start: 0,
                  end: Number(cadence.duration || 0) * 1000,
                }),
                { format: ["days", "hours", "minutes"] }
              )
            : null
      ),
    },
    ...(hide?.includes("Next Execution")
      ? []
      : [
          {
            label: t("nextExecution"),
            value:
              order.status === "PAUSED" ? (
                <span className="mono color-grey">{t("paused")}</span>
              ) : isSkipping ? (
                priceThreshold ? (
                  <span className="mono nowrap">
                    at{" "}
                    <Decimal
                      subscript
                      decimals={12}
                      amount={priceThreshold}
                      clip
                    />
                  </span>
                ) : (
                  <span className="mono nowrap">
                    at {"<"} {maximumSlippageBps / 100}% impact
                  </span>
                )
              ) : (
                <span
                  className={clsx("mono nowrap", {
                    "color-orange":
                      remainingSwaps > 0 && secondsUntilNextExecution < -5,
                    "color-darkOrange":
                      remainingSwaps > 0 && secondsUntilNextExecution < -30,
                    "color-darkRed fw-500":
                      remainingSwaps > 0 && secondsUntilNextExecution < -120,
                  })}>
                  {nextExecution}
                </span>
              ),
          },
        ]),
    {
      label: t("swapAmount"),
      value: swapAsset(BigInt(swapAmount.amount)),
    },
    {
      label: t("priceImpact"),
      value: stat(`${maximumSlippageBps / 100}%`),
    },
    {
      label: t("priceLimit"),
      value:
        priceThreshold > 0 ? (
          <Decimal subscript decimals={12} amount={priceThreshold} clip />
        ) : side === Side.Quote ? (
          <span className="mono color-lightGrey">∞</span>
        ) : (
          <Decimal
            subscript
            decimals={12}
            amount={0n}
            round={2}
            className="color-lightGrey"
            clip
          />
        ),
    },
    {
      label: t("remaining"),
      value: swapAsset(remainingSwapBalance),
    },
    {
      label: t("unclaimed"),
      value: (
        <Decimal
          subscript
          amount={acquiredReceiveAmount}
          decimals={minimumReceiveAmount.asset.metadata.decimals}
          symbol={minimumReceiveAmount.asset.metadata.symbol}
          symbolClassName="decimal__symbol--small color-grey condensed fw-500"
          clip
        />
      ),
    },
  ];

  return (
    <OrderRow
      icons={
        <>
          <IconDenom denom={swapAmount.asset.metadata.symbol} />
          <IconDenom denom={minimumReceiveAmount.asset.metadata.symbol} />
        </>
      }
      title={
        <span className="flex ai-b">
          {label(swapAmount.asset)}
          <Icons.ArrowRight
            className="as-c w-2 h-1.5 color-grey"
            style={{ marginInline: "0.05rem" }}
          />
          {label(minimumReceiveAmount.asset)}
        </span>
      }
      label={t("swapFromTo", {
        from: assetLabel(swapAmount.asset),
        to: assetLabel(minimumReceiveAmount.asset),
      })}
      tags={
        <>
          <span className="order-row__detail">
            <span className="mobile-card__label">{t("status")}</span>
            <span className="order-row__value">
              <LabelStatus {...order} completed={completed} plain />
            </span>
          </span>
          <span className="order-row__detail">
            <span className="mobile-card__label">{t("routes")}</span>
            <span className="order-row__value flex nowrap ai-c gap-0.5">
              <span className="mono color-pink">FIN</span>
              {!!routes.find(
                (r) => r.__typename === "CalcSwapRouteThorchain"
              ) && <span className="mono color-teal">TC</span>}
            </span>
          </span>
          <span className="order-row__detail">
            <span className="mobile-card__label">{t("swapMultiplier")}</span>
            <span
              className={clsx("order-row__value mono nowrap", {
                "color-teal":
                  swapAdjustment.__typename ===
                  "CalcSwapAmountAdjustmentLinearScalar",
              })}>
              {swapAdjustmentDescription}
            </span>
          </span>
        </>
      }
      aside={completed ? undefined : progressAside}
      fillAside
      primary={swapAsset(remainingSwapBalance)}
      headAction={claimButton}
      details={details}
      columns={3}
      actionsLastColumn
      actions={
        <>
          <PairLink to={tradeTo} label={t("viewPair")} />
          {actions}
        </>
      }
    />
  );
};

export const UnknownRecurringOrderRow: FC<{
  order: RecurringOrder;
  actions: ReactNode;
}> = ({ order, actions }) => (
  <OrderRow
    title={<span className="nowrap">{order.label}</span>}
    label={order.label}
    tags={<LabelStatus {...order} />}
    details={[]}
    actions={actions}
  />
);
